import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  getRemoteMediaDimensions,
  type ImageDimensions,
} from '../utils/imageDimensions';
import {
  hasAnyPreview,
  validateBannerDimensions,
  validateEditorsChoiceGraphic,
  validatePreviewDimensions,
} from '../utils/imageValidation';

interface UsePreviewFileHandlerProps {
  formPreviews: (File | null)[];
  existingUrls?: (string | null)[];
  onFileChange: (index: number, file: File | null) => void;
  onDimensionsChange?: (dimensions: ImageDimensions | null) => void;
  onFormTouched?: () => void;
}

interface UseSingleFileHandlerProps {
  onFileChange: (
    fieldName: 'dappIcon' | 'banner' | 'editorsChoiceGraphic',
    file: File | null,
  ) => void;
  onFormTouched?: () => void;
}

export const usePreviewFileHandler = ({
  formPreviews,
  existingUrls,
  onFileChange,
  onDimensionsChange,
  onFormTouched,
}: UsePreviewFileHandlerProps) => {
  const [previewDimensions, setPreviewDimensions] =
    useState<ImageDimensions | null>(null);
  const firstExistingUrl = existingUrls?.find((url) => url !== null) ?? null;
  const hasFilePreview = formPreviews.some((preview) => preview !== null);

  useEffect(() => {
    if (!firstExistingUrl) {
      return;
    }

    let cancelled = false;
    setPreviewDimensions(null);

    void getRemoteMediaDimensions(firstExistingUrl)
      .then((dimensions) => {
        if (!cancelled) {
          setPreviewDimensions(dimensions);
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [firstExistingUrl]);

  useEffect(() => {
    if (!firstExistingUrl && !hasFilePreview) {
      setPreviewDimensions(null);
    }
  }, [firstExistingUrl, hasFilePreview]);

  const handlePreviewFileChange = async (index: number, file: File | null) => {
    onFormTouched?.();

    if (file) {
      const isFirstPreview = !hasAnyPreview(formPreviews, existingUrls);
      let baselineDimensions = previewDimensions;

      if (!isFirstPreview && !baselineDimensions && firstExistingUrl) {
        try {
          baselineDimensions = await getRemoteMediaDimensions(firstExistingUrl);
          setPreviewDimensions(baselineDimensions);
        } catch {
          toast.error('Failed to validate existing preview dimensions');
          return;
        }
      }

      if (!isFirstPreview && !baselineDimensions) {
        toast.error('Wait for existing previews to finish loading');
        return;
      }

      const validation = await validatePreviewDimensions(
        file,
        isFirstPreview ? null : baselineDimensions,
      );

      if (!validation.isValid) {
        return;
      }

      if (isFirstPreview && validation.dimensions) {
        setPreviewDimensions(validation.dimensions);
        onDimensionsChange?.(validation.dimensions);
      }

      onFileChange(index, file);
    } else {
      // File removed
      onFileChange(index, null);

      if (!hasAnyPreview(formPreviews, existingUrls, index)) {
        setPreviewDimensions(null);
        onDimensionsChange?.(null);
      }
    }
  };

  return {
    handlePreviewFileChange,
    previewDimensions,
  };
};

export const useSingleFileHandler = ({
  onFileChange,
  onFormTouched,
}: UseSingleFileHandlerProps) => {
  const handleSingleFileChange = async (
    fieldName: 'dappIcon' | 'banner' | 'editorsChoiceGraphic',
    file: File | null,
  ) => {
    onFormTouched?.();

    if (fieldName === 'editorsChoiceGraphic' && file) {
      const validation = await validateEditorsChoiceGraphic(file);
      if (!validation.isValid) {
        return;
      }
    }

    if (fieldName === 'banner' && file) {
      const validation = await validateBannerDimensions(file);
      if (!validation.isValid) {
        return;
      }
    }

    onFileChange(fieldName, file);
  };

  return handleSingleFileChange;
};
